import axios, { AxiosResponse } from 'axios';
import { PickupSummary } from '../models/PickupSummary';
import { PickupDetailed } from '../models/PickupDetailed';
import { PickupNewOnCall } from '../models/PickupNewOnCall';
import { RoutePickup, RoutePickupStatus } from '../models/RoutePickup';
import { ReroutePickupsRequest, PickupStop } from '../models/ReroutePickupsRequest';

import { getCurrentDate } from '../functions/datetime';

const baseUrl = process.env.REACT_APP_ENVIRONMENT === 'Development' ? process.env.REACT_APP_API_URL : window.origin;

// If session has timed out, API redirects to login screen so HTML is returned to Axios and
// perceived as a 200 OK. This checks to see if the URL of the response matched this redirect
// and if so, redirects them to that URL
const handleRedirect = (responseURL: string) => {
  if (responseURL && responseURL.endsWith('SessionTimeout')) {
    window.location.replace(responseURL);
  }

  return;
};

export const GetNewPickups = async (): Promise<PickupNewOnCall[]> => {
  const url = baseUrl + '/Pickup/NewOncallPickups';

  try {
    const {
      data,
      request: { responseURL },
    } = await axios.get<PickupNewOnCall[]>(url);
    handleRedirect(responseURL);

    return data;
  } catch (e) {
    console.log(e);
  }

  return [];
};

export const GetPickupsSummary = async (): Promise<PickupSummary[]> => {
  const PuDate = getCurrentDate();
  const url = baseUrl + '/Pickup/PURouteStatusSummaryByPUDate';

  try {
    const {
      data,
      request: { responseURL },
    } = await axios.get<PickupSummary[]>(url, { params: { PuDate } });
    handleRedirect(responseURL);

    if (responseURL && responseURL.endsWith('SessionTimeout')) {
      window.location.replace(responseURL);
    }
    return data;
  } catch (e) {
    console.log(e);
  }

  return [];
};

export const GetRouteDetails = async (route: string): Promise<RoutePickup[]> => {
  const url = baseUrl + `/Pickup/PickupSummaryByRouteandPickupDate`;

  try {
    const PickupDate = getCurrentDate();
    const {
      data,
      request: { responseURL },
    } = await axios.get<RoutePickup[]>(url, { params: { RouteNo: route, PickupDate } });
    handleRedirect(responseURL);

    return data;
  } catch (e) {
    console.log(e);
  }

  return [];
};

export const GetRouteStatuses = async (route: string): Promise<RoutePickupStatus[]> => {
  const url = baseUrl + `/Pickup/PURouteStatusDetailByRouteandPUDate`;

  try {
    const PUDate = getCurrentDate();

    const {
      data,
      request: { responseURL },
    } = await axios.get<RoutePickupStatus[]>(url, { params: { RouteNo: route, PUDate } });
    handleRedirect(responseURL);

    return data;
  } catch (e) {
    console.log(e);
  }

  return [];
};

export const GetPickupDetails = async (StopId: number, StopType: string): Promise<PickupDetailed | false> => {
  const url = baseUrl + `/Pickup/PickupStopDetails`;

  try {
    const PickupDate = getCurrentDate();

    const {
      data,
      request: { responseURL },
    } = await axios.get<PickupDetailed>(url, {
      params: { StopId, StopType: StopType.slice(0, 1), PickupDate },
    });
    handleRedirect(responseURL);

    return data;
  } catch (e) {
    console.log(e);
  }

  return false;
};

export const ReroutePickups = async (fromRoute: string = 'NA', toRoute: string, stops: PickupStop[]) => {
  const url = baseUrl + `/pickup/reroute`;

  try {
    const payload = new FormData();
    payload.append('FromRoute', fromRoute);
    payload.append('ToRoute', toRoute);

    stops.forEach((stop, idx) => {
      payload.append(`PickupStops[${idx}][StopId]`, stop.locationId.toString());
      payload.append(`PickupStops[${idx}][StopType]`, stop.type.slice(0, 1)); // Char
    });

    const config = { headers: { 'Content-Type': 'multipart/form-data' } };

    const {
      data,
      request: { responseURL },
    } = await axios.post<ReroutePickupsRequest | string>(url, payload, config);
    handleRedirect(responseURL);

    if (typeof data === 'string' && data.startsWith('You cannot perform route edit')) {
      alert(data)
      return false
    }

    return data;
  } catch (e) {
    console.log(e)
  }

  return [];
};
